import { useEffect, useRef, useState, type RefObject } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { DevTool } from "@hookform/devtools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import {
  Form,
  Button,
  DatePicker,
  Input,
  Typography,
  Select,
  Radio,
  Divider,
  Spin,
  notification,
} from "antd";
import dayjs from "dayjs";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import {
  journalsQueries,
  isSectionFourNoContent,
  JournalRecordPageShell,
  SECTION_FOUR_CREATE_ORG_REQUIRED_HINT,
  userHasOrganizationOnTitlePage,
} from "@/entities/journal";
import { useCoordinator, useLocalStorage } from "@/shared/lib/hooks";
import { useUnifiedAutoResize } from "@/shared/utils";
import { useAppRoutes } from "@shared/lib/react-router";
import { GeneralInfoPlace } from "../../../journals-section-third/ui/journal-section-form/ui/section-general-info/ui";
import {
  buildSectionFourSignersList,
  formatSectionFourSignerLabel,
} from "../../model/utils/format-section-four-signer-label";
import { useModalInfo } from "../journal-section-form/model";
import { useCreateSectionFourEntry } from "../journal-section-form/model/hooks/use-create-section-four-entry";
import { useUpdateSectionFourEntry } from "../journal-section-form/model/hooks/use-update-section-four-entry";
import { useUpdateSectionFourEntrySigned } from "../journal-section-form/model/hooks/use-update-section-four-entry-signed";
import {
  JournalSectionFormEditSchema,
} from "../journal-section-form/model/types/schema";
import {
  toDateOnlyIso,
  uploadConfigFour,
  uploadConfigMultiFour,
} from "../journal-section-form/model/utils";
import {
  getControlEndDisabledDate,
  getControlEndDisabledTime,
} from "../journal-section-form/model/utils/control-event-period-date-restrictions";
import { SectionCompletedWorks } from "../journal-section-form/ui";
import { SectionDocumentFourList } from "../journal-section-form/ui/journal-section-form/ui/section-document-four-list";
import "./journal-section-form-page.scss";

type JournalSectionFormEditSchemaType = z.infer<
  typeof JournalSectionFormEditSchema
>;

const { Title } = Typography;
const { Option } = Select;

export const JournalSectionFormPage = () => {
  const navigate = useNavigate();
  const { itemNoVersionId: itemNoVersionIdParam } = useParams<{ itemNoVersionId?: string }>();
  const isEditMode = itemNoVersionIdParam && itemNoVersionIdParam !== "new";
  const itemNoVersionId = isEditMode ? itemNoVersionIdParam : undefined;
  const [defectsExist, setDefectsExist] = useState<string>("false");
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<JournalSectionFormEditSchemaType>({
    resolver: zodResolver(JournalSectionFormEditSchema),
    defaultValues: {
      defectsExist: "false",
      constructionControlInfo: "",
      actionDetails: "",
    },
  });

  // Автоматическое обновление высоты iframe в unified режиме
  const wrapperRef = useRef<HTMLDivElement>(null);
  useUnifiedAutoResize(wrapperRef as RefObject<HTMLElement>);

  const { mainJournalId, titleId, projectId, objectId } = useCoordinator();
  const appRoutes = useAppRoutes();
  const [newToken] = useLocalStorage("newToken");

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const section4ListId =
    mainJournalData?.mainJournal?.section4?.sectionListId || "";

  const { data: sectionFourData, isSuccess: isSectionFourLoaded } = useQuery({
    ...journalsQueries.getSection4({
      section4ListId,
      page: 1,
      limit: 1,
    }),
    enabled: !isEditMode && !!section4ListId,
  });

  const { data: titleJournal, isSuccess: isTitleJournalLoaded } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  useEffect(() => {
    if (isEditMode) return;

    const redirectToSectionList = (description: string) => {
      notification.warning({
        message: "Доступ ограничен",
        description,
      });
      navigate(
        appRoutes.journals.section4(projectId, objectId, mainJournalId, titleId),
      );
    };

    if (isSectionFourLoaded) {
      const isListAccessRestricted =
        sectionFourData == null || isSectionFourNoContent(sectionFourData);

      if (isListAccessRestricted) {
        redirectToSectionList(
          "Добавьте свою организацию в титульный лист, чтобы добавлять записи в раздел 4.",
        );
        return;
      }
    }

    if (isTitleJournalLoaded && !userHasOrganizationOnTitlePage(titleJournal, newToken)) {
      redirectToSectionList(SECTION_FOUR_CREATE_ORG_REQUIRED_HINT);
    }
  }, [
    isEditMode,
    isSectionFourLoaded,
    isTitleJournalLoaded,
    sectionFourData,
    titleJournal,
    newToken,
    navigate,
    appRoutes,
    projectId,
    objectId,
    mainJournalId,
    titleId,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: addRecord, isPending } = useCreateSectionFourEntry({
    sectionFourId: mainJournalData?.mainJournal?.section4?.sectionId || "",
    section4ListId: mainJournalData?.mainJournal?.section4?.sectionListId || "",
  });

  const { mutate: updateRecord, isPending: isUpdatePending } =
    useUpdateSectionFourEntry({
      section4ListId:
        mainJournalData?.mainJournal?.section4?.sectionListId || "",
      itemNoVersionId: itemNoVersionId || "",
    });

  const { data: itemInfo, isFetching } = useModalInfo(itemNoVersionId || "", {
    enabled: isEditMode || false,
  });

  const { mutate: updateRecordSigned, isPending: isUpdateSignedPending } =
    useUpdateSectionFourEntrySigned({
      section4ListId:
        mainJournalData?.mainJournal?.section4?.sectionListId || "",
      versionId: itemInfo?.currentVersionId || "",
      itemPartOneId: itemInfo?.itemPartOne?.id || "",
      itemNoVersionId: itemNoVersionId || "",
    });

  const signersList = buildSectionFourSignersList(
    titleJournal?.mainJournal?.titleJournal?.developer?.constructionControls,
    titleJournal?.mainJournal?.titleJournal?.technicalCustomer
      ?.constructionControls,
  );
  const originalDocsRef = useRef<any[]>([]);

  const docsConverted = itemInfo?.itemPartOne?.docs?.map((doc: any) => ({
    id: doc.id,
    docId: doc.docId!,
    docType: doc.docType!,
    docTitle: doc.name!,
    documentUrl: doc.documentUrl!,
    docInfo: doc.docInfo,
  }));

  useEffect(() => {
    if (isEditMode && itemNoVersionId && itemInfo?.itemPartOne) {
      const part = itemInfo?.itemPartOne;

      setValue("defectsExist", part.isDefects ? "true" : ("false" as any), {
        shouldDirty: true,
        shouldTouch: true,
      });
      setDefectsExist(String(part.isDefects));

      setValue("constructionControlInfo", part?.constructionControlInfo || "", {
        shouldDirty: true,
        shouldTouch: true,
      });

      setValue(
        "controlStartDate",
        part?.controlEventPeriod?.beginDate || (new Date() as any),
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      );
      setValue(
        "controlEndDate",
        part?.controlEventPeriod?.endDate || (new Date() as any),
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      );

      setValue("defectsAbsenceInfo", part.defectsAbsenceRecord || "", {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(
        "defectFixingPlanDate",
        part?.defectFixingPlanDate || undefined,
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      );
      setValue("defectDescriptionText", part.defectDescriptionText || "", {
        shouldDirty: true,
        shouldTouch: true,
      });

      originalDocsRef.current = docsConverted || [];

      setValue(
        "docs",
        (docsConverted || []).filter(
          (doc: any) => doc.docId !== undefined && doc.docType !== undefined,
        ),
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      );
      setValue("works", part.works || ([] as any), {
        shouldDirty: true,
        shouldTouch: true,
      });

      const mappedOtherDocs =
        part.otherDocuments?.map((doc: any, index: number) => ({
          fileMetadata: {
            fileNumber: index,
            fileName: doc.fileName || "",
            fileType: "application/pdf",
            fileContentLength: 0,
            id: doc.id,
            fileId: doc.file_id,
          },
        })) || [];

      setValue("itemPartOne.otherDocuments", mappedOtherDocs || ([] as any), {
        shouldDirty: true,
        shouldTouch: true,
      });

      setValue("structuralElementName", part.structuralElementName || "", {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("locationDescription", part.locationDescription || "", {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("locationType", part.locationType || ("LOCATION" as any), {
        shouldDirty: true,
        shouldTouch: true,
      });

      setValue(
        "controlRepresentative",
        part.inspectionEmployee?.inspectionEmployeeId || "",
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      );
    }
  }, [itemInfo, itemNoVersionId, setValue, docsConverted, isEditMode]);

  const onSubmit = (data: any) => {
    const transformedWorks = (data.works || []).map((work: any) => ({
      itemNoVersionId: work.info?.itemNoVersionId ?? work.itemNoVersionId,
      section3ListId:
        mainJournalData?.mainJournal?.section3?.sectionListId ?? "",
      selected: true,
    }));

    if (isEditMode && itemNoVersionId) {
      const otherDocsMeta = data?.itemPartOne?.otherDocuments || [];
      const allFiles = getValues("itemPartOne.files") || [];
      const originalOtherDocs = itemInfo?.itemPartOne?.otherDocuments || [];

      const createOtherDocs = otherDocsMeta
        .map((doc: any, i: number) => ({
          ...doc,
          file: allFiles[i],
        }))
        .filter((doc: any) => !doc.fileMetadata?.id);

      const deleteOtherDocs = originalOtherDocs
        .filter((orig: any) => {
          return !otherDocsMeta.some(
            (doc: any) =>
              doc.fileMetadata?.fileId === orig.file_id &&
              doc.fileMetadata?.fileName === orig.fileName,
          );
        })
        .filter((removed: any) => removed.id && removed.file_id)
        .map((removed: any) => ({
          file: {
            id: removed.id as string,
            fileId: removed.file_id as string,
          },
        }));

      const commonPayload = {
        mainJournalId: mainJournalData?.mainJournal?.mainJournalId ?? "",
        section4Id: mainJournalData?.mainJournal?.section4?.sectionId ?? "",
        section4ListId:
          mainJournalData?.mainJournal?.section4?.sectionListId ?? "",
        itemPartOne: {
          isDefects: defectsExist === "true",
          constructionControlInfo: data.constructionControlInfo,
          controlEventPeriod: {
            beginDate: new Date(data.controlStartDate).toISOString(),
            endDate: new Date(data.controlEndDate).toISOString(),
          },
          defectsAbsenceRecord: data.defectsAbsenceInfo || "",
          ...(defectsExist === "true"
            ? {
                ...(data.defectFixingPlanDate
                  ? {
                      defectFixingPlanDate: toDateOnlyIso(
                        data.defectFixingPlanDate,
                      ),
                    }
                  : {}),
              }
            : {}),
          defectDescriptionText: data.defectDescriptionText || "",
          updateDocs: [
            ...data.docs
              .filter((doc: any) => doc.docId)
              .filter(
                (doc: any) =>
                  !docsConverted?.some(
                    (original: any) =>
                      original.docId === doc.docId &&
                      original.docType === doc.docType,
                  ),
              )
              .map((doc: any) => ({
                docId: doc.docId,
                docType: doc.docType,
                action: "CREATE",
                docInfo: doc.docInfo,
              })),
            ...(docsConverted
              ?.filter(
                (original: any) =>
                  !data.docs.some(
                    (doc: any) =>
                      doc.docId === original.docId &&
                      doc.docType === original.docType,
                  ),
              )
              .map((removed: any) => ({
                id: removed?.id,
                docId: removed.docId,
                docType: removed.docType,
                docInfo: removed.docInfo,
                action: "DELETE",
              })) || []),
          ],
          updateWorks: transformedWorks,
          structuralElementName: data.structuralElementName || "",
          locationDescription: data.locationDescription || "",
          locationType: data.locationType || "LOCATION",
          inspectionEmployeeId: data.controlRepresentative,
          createOtherDocs,
          deleteOtherDocs,
        },
      };

      const isSigned = itemInfo?.itemPartOne?.status === "SIGNED";

      if (isSigned) {
        updateRecordSigned(
          {
            ...commonPayload,
            itemNoVersionId: itemNoVersionId || "",
            itemPartOne: {
              ...commonPayload.itemPartOne,
              actionDetails: data.actionDetails || "",
            },
          },
          {
            onSuccess: () => {
              reset();
              navigateToSectionList();
            },
          },
        );
      } else {
        updateRecord(
          {
            ...commonPayload,
          },
          {
            onSuccess: () => {
              reset();
              navigateToSectionList();
            },
          },
        );
      }
    } else {
      const otherDocsMeta = data?.itemPartOne?.otherDocuments || [];

      const preparedOtherDocuments = otherDocsMeta.map(
        (doc: any, index: number) => ({
          ...doc,
          file: getValues("itemPartOne.files")?.[index],
        }),
      );

      addRecord(
        {
          mainJournalId: mainJournalData?.mainJournal?.mainJournalId ?? "",
          section4Id: mainJournalData?.mainJournal?.section4?.sectionId ?? "",
          section4ListId:
            mainJournalData?.mainJournal?.section4?.sectionListId ?? "",
          itemPartOne: {
            isDefects: defectsExist === "true",
            constructionControlInfo: data.constructionControlInfo,
            controlEventPeriod: {
              beginDate: new Date(data.controlStartDate).toISOString(),
              endDate: new Date(data.controlEndDate).toISOString(),
            },
            defectsAbsenceRecord: data.defectsAbsenceInfo || "",
            ...(defectsExist === "true"
              ? {
                  defectRecordDate: new Date().toISOString(),
                  ...(data.defectFixingPlanDate
                    ? {
                        defectFixingPlanDate: toDateOnlyIso(
                          data.defectFixingPlanDate,
                        ),
                      }
                    : {}),
                }
              : {}),
            defectDescriptionText: data.defectDescriptionText || "",
            docs: data?.docs || [],
            works: transformedWorks,
            otherDocuments: preparedOtherDocuments,
            structuralElementName: data.structuralElementName || "",
            locationDescription: data.locationDescription || "",
            locationType: data.locationType || "LOCATION",
            inspectionEmployeeId: data.controlRepresentative,
          },
        },
        {
          onSuccess: () => {
            reset();
            navigateToSectionList();
          },
        },
      );
    }
  };
  const watchStartDate = useWatch({
    control,
    name: "controlStartDate",
  });

  const isSaving = isPending || isUpdatePending || isUpdateSignedPending;
  const isFormLoading = Boolean(isEditMode && isFetching && !itemInfo);
  const showPageLoader = isFormLoading || isSaving;
  const loaderTip = isSaving ? "Сохранение..." : "Загрузка...";

  const navigateToSectionList = () => {
    navigate(
      appRoutes.journals.section4(projectId, objectId, mainJournalId, titleId),
    );
  };

  const handleClose = () => {
    if (showPageLoader) return;
    navigateToSectionList();
  };

  const title = isEditMode ? "Редактирование записи" : "Добавление записи в раздел 4 ОЖР";

  return (
    <JournalRecordPageShell ref={wrapperRef} onClose={handleClose}>
        <Spin
          spinning={showPageLoader}
          size="large"
          tip={loaderTip}
          className="journal-section-form-page-spin"
        >
          <div className="journal-section-form-page-title-block">
            <Title level={3}>
              {title}
            </Title>
          </div>

            {isEditMode && itemInfo?.itemPartOne?.status === "SIGNED" && (
              <div className="journal-edit-notice-box">
                <div className="journal-edit-notice-text">
                  В случае внесения изменений в записи общего журнала в
                  электронной форме создается новая версия записи. Запись нужно
                  будет подписать заново. Понадобится заполнить поле о причинах
                  изменений.
                </div>
              </div>
            )}

            <Form onFinish={handleSubmit(onSubmit)} layout="vertical">
              {/* Сведения о проведении строительного контроля */}
              <Title level={5} style={{ marginBottom: "24px" }}>
                Сведения о контрольной процедуре
              </Title>
              <Form.Item
                label="Сведения о проведении строительного контроля"
                validateStatus={errors.constructionControlInfo ? "error" : ""}
                help={errors.constructionControlInfo?.message?.toString()}
                style={{ marginBottom: 24 }}
              >
                <Controller
                  name="constructionControlInfo"
                  control={control}
                  // rules={{ required: "Укажите сведения об отсутствии недостатков" }}
                  render={({ field }) => (
                    <Input.TextArea
                      {...field}
                      placeholder="Введите сведения"
                      rows={2}
                      maxLength={255}
                      showCount
                    />
                  )}
                />
              </Form.Item>
              {/* Сроки проведения контрольного мероприятия */}
              <Form.Item
                label="Сроки проведения контрольного мероприятия"
                style={{ marginBottom: 24 }}
                validateStatus={
                  errors.controlStartDate || errors.controlEndDate
                    ? "error"
                    : ""
                }
                help={(
                  errors.controlStartDate?.message ||
                  errors.controlEndDate?.message
                )?.toString()}
              >
                <div className="journal-form-date-range">
                  <Controller
                    name="controlStartDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date?.toDate() ?? null)
                        }
                        format="DD.MM.YYYY HH:mm"
                        style={{ width: "50%" }}
                        showTime
                      />
                    )}
                  />

                  <Controller
                    name="controlEndDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date?.toDate() ?? null)
                        }
                        format="DD.MM.YYYY HH:mm"
                        style={{ width: "50%" }}
                        showTime
                        disabled={!watchStartDate}
                        disabledDate={getControlEndDisabledDate(watchStartDate)}
                        disabledTime={getControlEndDisabledTime(watchStartDate)}
                      />
                    )}
                  />
                </div>
              </Form.Item>

              {/* Недостатки */}
              {!isEditMode ? (
                <Form.Item label="Недостатки" style={{ marginBottom: 16 }}>
                  <Radio.Group
                    value={defectsExist}
                    onChange={(e) => {
                      const { value } = e.target;
                      setDefectsExist(value);
                      setValue("defectsExist", value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                  >
                    <Radio value="true">Выявлены</Radio>
                    <Radio value="false">Не выявлены</Radio>
                  </Radio.Group>
                </Form.Item>
              ) : null}
              {/* Сведения об отсутствии недостатков */}
              {defectsExist === "false" && (
                <Form.Item
                  label="Сведения об отсутствии недостатков"
                  validateStatus={errors.defectsAbsenceInfo ? "error" : ""}
                  help={errors.defectsAbsenceInfo?.message?.toString()}
                  style={{ marginBottom: 24 }}
                >
                  <Controller
                    name="defectsAbsenceInfo"
                    control={control}
                    render={({ field }) => (
                      <Input.TextArea
                        {...field}
                        placeholder="Например: Недостатки не выявлены, работы выполнены корректно"
                        rows={2}
                        maxLength={255}
                        showCount
                      />
                    )}
                  />
                </Form.Item>
              )}
              {defectsExist === "true" && (
                <>
                  <Form.Item
                    label="Описание выявленных недостатков"
                    validateStatus={errors.defectDescriptionText ? "error" : ""}
                    help={errors.defectDescriptionText?.message?.toString()}
                    style={{ marginBottom: 24 }}
                  >
                    <Controller
                      name="defectDescriptionText"
                      control={control}
                      rules={{ required: "Поле обязательно для заполнения" }}
                      render={({ field }) => (
                        <Input.TextArea
                          {...field}
                          status={errors.defectDescriptionText ? "error" : ""}
                          placeholder="Например: Недостатки  выявлены, работы выполнены не корректно"
                          rows={2}
                          maxLength={255}
                          showCount
                        />
                      )}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Срок устранения"
                    validateStatus={errors.defectFixingPlanDate ? "error" : ""}
                    help={errors.defectFixingPlanDate?.message?.toString()}
                    style={{ marginBottom: 24 }}
                  >
                    <Controller
                      name="defectFixingPlanDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) =>
                            field.onChange(date?.toDate() ?? null)
                          }
                          format="DD.MM.YYYY"
                          style={{ width: "25%" }}
                        />
                      )}
                    />
                  </Form.Item>

                  <Title level={5} style={{ marginBottom: "24px" }}>
                    Описание предмета контроля (необязательно)
                  </Title>

                  <Form.Item
                    label="Наименование и обозначение структурного элемента объекта капитального строительства"
                    style={{ marginBottom: 24 }}
                  >
                    <Controller
                      name="structuralElementName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          status={errors.structuralElementName ? "error" : ""}
                          placeholder="Введите наименование структурного элемента"
                          maxLength={255}
                          showCount
                        />
                      )}
                    />
                    {errors.structuralElementName && (
                      <div className="journal-form-error--inline">
                        {errors.structuralElementName.message?.toString()}
                      </div>
                    )}
                  </Form.Item>

                  <GeneralInfoPlace
                    // @ts-expect-error control/setValue типы формы раздела 3 и 4 не совпадают
                    control={control}
                    // @ts-expect-error control/setValue типы формы раздела 3 и 4 не совпадают
                    setValue={setValue}
                    errors={errors}
                    initialLocationType={itemInfo?.itemPartOne?.locationType}
                  />

                  <SectionCompletedWorks
                    control={control}
                    setValue={setValue}
                    fieldName="works"
                  />

                  <SectionDocumentFourList
                    uploadConfigFour={uploadConfigFour}
                    uploadConfigMultiFour={uploadConfigMultiFour}
                    setValue={setValue}
                    errors={errors}
                    getValues={getValues}
                    control={control}
                    isOtherDocs
                    isMultiple={false}
                  />
                  <SectionDocumentFourList
                    uploadConfigMultiFour={uploadConfigMultiFour}
                    title="Документы подтверждающие факт нарушения (необязательно)"
                    setValue={setValue}
                    errors={errors}
                    getValues={getValues}
                    control={control}
                    isOtherDocs={false}
                    isMultiple
                  />
                </>
              )}
              <Form.Item
                label="Уполномоченный представитель по вопросам строительного контроля"
                validateStatus={errors.controlRepresentative ? "error" : ""}
                help={errors.controlRepresentative?.message?.toString()}
                style={{ marginBottom: 24 }}
              >
                <Controller
                  name="controlRepresentative"
                  control={control}
                  rules={{ required: "Выберите представителя" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Выберите представителя"
                      optionLabelProp="label"
                    >
                      {signersList.map((signer) => {
                        const label = formatSectionFourSignerLabel(signer);

                        return (
                          <Option
                            key={signer.user?.userId}
                            value={signer?.user?.userId}
                            label={label}
                          >
                            {label}
                          </Option>
                        );
                      })}
                    </Select>
                  )}
                />
                <div className="journal-form-hint">
                  Этому человеку будет отправлена данная запись на подписание
                </div>
              </Form.Item>

              {isEditMode && itemInfo?.itemPartOne?.status === "SIGNED" && (
                <Form.Item
                  label="Причина изменения записи"
                  validateStatus={errors.actionDetails ? "error" : ""}
                  help={errors.actionDetails?.message?.toString()}
                  style={{ marginBottom: 24 }}
                >
                  <Controller
                    name="actionDetails"
                    control={control}
                    rules={{ required: "Укажите причину изменения записи" }}
                    render={({ field }) => (
                      <Input.TextArea
                        {...field}
                        placeholder="Опишите причину изменения подписанной записи"
                        rows={2}
                        maxLength={255}
                        showCount
                      />
                    )}
                  />
                </Form.Item>
              )}

              <DevTool control={control} />
              <Divider />
              {/* Кнопки */}
              <Form.Item style={{ marginBottom: 24 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ height: 44, marginRight: 8 }}
                  disabled={showPageLoader}
                >
                  {isEditMode ? "Обновить запись" : "Добавить запись"}
                </Button>
                <Button
                  onClick={handleClose}
                  disabled={showPageLoader}
                  style={{ marginLeft: 8, height: 44 }}
                >
                  Отмена
                </Button>
              </Form.Item>
            </Form>
        </Spin>
    </JournalRecordPageShell>
  );
};

